import { Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { DatePicker } from "@mui/x-date-pickers"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardMerchantsMultiselectFilter } from "../components/StandardMerchantsMultiselectFilter"
import { StandardMultiMerchantShopsFilter } from "../components/StandardMultiMerchantShopsFilter"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { StandardPaymentMethodsFilter } from "../components/StandardPaymentMethodsFilter"
import { StandardMerchantShopsFilter } from "../components/StandardMerchantShopsFilter"
import { selectEffectiveUserType } from "../../../../modules/context/contextSelectors"
import { StandardLast4NumbersFilter } from "../components/StandardLast4NumbersFilter"
import useActiveFiltersCountAccounting from "./useActiveFiltersCountAccounting"
import { StandardCustomerSelect } from "../components/StandardCustomerSelect"
import { StandardEmailFilter } from "../components/StandardEmailFilter"
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import { CustomFilterMenuAccordion, FilterMenu } from "../FilterMenu"
import { StandardIdFilter } from "../components/StandardIdFilter"
import { UserType } from "../../../../modules/login/loginTypes"
import useFilterAccordions from "../hooks/useFilterAccordions"

// TODO: Remove startDate and endDate as it does not exist in drawer
export type AccountingFilterProps = Readonly<
    { startDate: DateTime | null; endDate: DateTime | null } & Partial<{
        orderId: string
        merchantId: string
        merchantIds: string[]
        shopIds: string[]
        customerId: string
        userGroupId: string
        email: string
        paymentMethods: string[]
        employeeId: string
        creditCardLast4: string
    }>
>

export const defaultAccountingFilterOptionsList: AccountingFilterProps = {
    orderId: "",
    startDate: DateTime.now(),
    endDate: DateTime.now(),
    merchantId: "",
    merchantIds: [],
    shopIds: [],
    customerId: "",
    userGroupId: "",
    email: "",
    paymentMethods: [],
    creditCardLast4: "",
    employeeId: "",
}

type AccountingFiltersDrawerProps = PropsWithChildren<{
    drawerOpen: boolean
    setDrawerOpen: (val: boolean) => void
    setFiltersList: Dispatch<SetStateAction<AccountingFilterProps>>
    filtersList: AccountingFilterProps
}>

type FilterAccordions = {
    order: boolean
    period: boolean
    merchant: boolean
    debtor: boolean
    endUser: boolean
    payment: boolean
}

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
}))

export function AccountingFiltersDrawer({
    drawerOpen,
    setDrawerOpen,
    setFiltersList,
    filtersList,
    children,
}: AccountingFiltersDrawerProps) {
    const { classes } = useStyles()
    const [optionsList, setOptionsList] = useState<AccountingFilterProps>(filtersList)

    const { expandedAccordions, toggleAccordion } = useFilterAccordions<FilterAccordions>({
        order: false,
        period: false,
        merchant: false,
        debtor: false,
        endUser: false,
        payment: false,
    })

    const currentUserType = useAppSelector(selectEffectiveUserType)

    const { merchantActive, orderActive, endUserActive, debtorActive, paymentActive } =
        useActiveFiltersCountAccounting(optionsList)

    useEffect(() => {
        setOptionsList(filtersList)
    }, [filtersList])

    const handleMerchantClear = () => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
                customerId: "",
                merchantIds: [],
                shopIds: [],
            }
        })
    }

    const handleClearAll = () => {
        setOptionsList({
            ...defaultAccountingFilterOptionsList,
            merchantId: currentUserType === UserType.MERCHANT ? optionsList.merchantId : "",
        })
    }

    const handelClearByType = (type: keyof AccountingFilterProps) => {
        setOptionsList((prevOptions) => {
            return {
                ...prevOptions,
                [type]: defaultAccountingFilterOptionsList[type],
            }
        })
    }

    useDebouncedEffect(() => {
        setFiltersList(optionsList)
    }, [optionsList, setFiltersList])

    const handleClose = () => {
        setDrawerOpen(false)
    }

    return (
        <FilterMenu
            drawerOpen={drawerOpen}
            handleClose={handleClose}
            handleClearAll={handleClearAll}
            isShowClearAllButton={true}
        >
            <LocalizedStrict id="supplier-pos-table-filters-order-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Order"
                    handleClear={() => handelClearByType("orderId")}
                    counter={orderActive}
                    expanded={expandedAccordions.order}
                    toggleAccordion={() => toggleAccordion("order")}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardIdFilter
                            value={optionsList.orderId}
                            onChange={(orderId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    orderId,
                                }))
                            }
                            type="orderId"
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-pos-table-filters-merchant-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    expanded={expandedAccordions.merchant}
                    toggleAccordion={() => toggleAccordion("merchant")}
                    title="Merchant"
                    handleClear={handleMerchantClear}
                    counter={merchantActive}
                >
                    <Grid container direction="column" gap="1rem">
                        {currentUserType !== UserType.MERCHANT && (
                            <StandardMerchantsMultiselectFilter
                                value={optionsList.merchantIds || []}
                                setValue={(merchantIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        merchantIds: merchantIds,
                                    }))
                                }}
                            />
                        )}
                        {currentUserType === UserType.MERCHANT ? (
                            <StandardMerchantShopsFilter
                                merchantId={optionsList.merchantId}
                                value={optionsList.shopIds || []}
                                setValue={(shopIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        shopIds,
                                    }))
                                }}
                            />
                        ) : (
                            <StandardMultiMerchantShopsFilter
                                merchantIds={optionsList.merchantIds || []}
                                selectedIds={optionsList.shopIds || []}
                                setSelectedIds={(shopIds) => {
                                    setOptionsList((prev) => ({
                                        ...prev,
                                        shopIds,
                                    }))
                                }}
                            />
                        )}
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>

            <LocalizedStrict id="supplier-pos-table-filters-debtor-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Debtor"
                    expanded={expandedAccordions.debtor}
                    toggleAccordion={() => toggleAccordion("debtor")}
                    handleClear={() => {
                        handelClearByType("customerId")
                        handelClearByType("userGroupId")
                    }}
                    counter={debtorActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardCustomerSelect
                            setCustomerId={(customerId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    customerId,
                                }))
                            }
                            setCustomerGroupId={(userGroupId) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    userGroupId,
                                }))
                            }
                            customerId={optionsList.customerId}
                            customerGroupId={optionsList.userGroupId}
                            merchantIdValue={optionsList.merchantId}
                            isFullWidth
                            isFilterDrawer
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-pos-table-filters-end-user-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="End user"
                    expanded={expandedAccordions.endUser}
                    toggleAccordion={() => toggleAccordion("endUser")}
                    handleClear={() => {
                        handelClearByType("email")
                        handelClearByType("employeeId")
                    }}
                    counter={endUserActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardEmailFilter
                            value={optionsList.email}
                            onChange={(email) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    email,
                                }))
                            }}
                        />
                        <StandardIdFilter
                            value={optionsList.employeeId}
                            onChange={(employeeId) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    employeeId,
                                }))
                            }}
                            type="employeeId"
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            <LocalizedStrict id="supplier-pos-table-filters-payment-title" attrs={{ title: true }}>
                <CustomFilterMenuAccordion
                    title="Payment"
                    handleClear={() => {
                        handelClearByType("creditCardLast4")
                        handelClearByType("paymentMethods")
                    }}
                    expanded={expandedAccordions.payment}
                    toggleAccordion={() => toggleAccordion("payment")}
                    counter={paymentActive}
                >
                    <Grid container direction="column" gap="1rem">
                        <StandardPaymentMethodsFilter
                            value={optionsList.paymentMethods || []}
                            setValue={(paymentMethods) =>
                                setOptionsList((prev) => ({
                                    ...prev,
                                    paymentMethods,
                                }))
                            }
                        />
                        <StandardLast4NumbersFilter
                            value={optionsList.creditCardLast4}
                            onChange={(creditCardLast4) => {
                                setOptionsList((prev) => ({
                                    ...prev,
                                    creditCardLast4,
                                }))
                            }}
                        />
                    </Grid>
                </CustomFilterMenuAccordion>
            </LocalizedStrict>
            {children}
        </FilterMenu>
    )
}
